import type { ClusterSnapshot, GpuSeries, Job } from "../../types";
import { GaugeArc } from "../charts/GaugeArc";
import { MultiLineChart } from "../charts/MultiLineChart";
import { fmtDuration } from "../../utils/format";

interface Props {
  snapshot: ClusterSnapshot;
  jobIdVar: number;
  setJobIdVar: (id: number) => void;
  onDrillNode: (n: string) => void;
}

const PALETTE = [
  "var(--cyan)",
  "var(--violet)",
  "var(--amber)",
  "var(--blue)",
  "var(--emerald)",
  "var(--rose)",
];

const last = (arr: number[]): number => (arr.length ? arr[arr.length - 1] : 0);
const avg = (arr: number[], n = arr.length): number => {
  const slice = arr.slice(-n);
  return slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : 0;
};

export function JobPerformance({
  snapshot,
  jobIdVar,
  setJobIdVar,
  onDrillNode,
}: Props) {
  const job = snapshot.jobs.find((j) => j.id === jobIdVar) ?? snapshot.jobs[0];
  if (!job) {
    return (
      <div className="panel">
        <div
          className="panel-body"
          style={{ padding: 40, textAlign: "center", color: "var(--fg-2)" }}
        >
          No jobs in queue.
        </div>
      </div>
    );
  }
  const jobGpus: GpuSeries[] = job.gpus
    .map((gid) => snapshot.gpus.find((g) => g.id === gid))
    .filter((g): g is GpuSeries => Boolean(g));
  const runningJobs = snapshot.jobs.filter(
    (j) => j.state === "RUNNING" && j.gpus.length > 0
  );

  const avgUtilNow = jobGpus.length
    ? jobGpus.reduce((a, g) => a + avg(g.util, 120), 0) / jobGpus.length
    : 0;
  const peakUtil = jobGpus.length
    ? Math.max(...jobGpus.map((g) => Math.max(...g.util.slice(-120))))
    : 0;
  const totalPower = jobGpus.reduce((a, g) => a + last(g.power_w), 0);
  const totalMem = jobGpus.reduce((a, g) => a + last(g.mem_mb), 0) / 1024;
  const efficiency = avgUtilNow;
  const effColor =
    efficiency > 75
      ? "var(--emerald)"
      : efficiency > 45
      ? "var(--amber)"
      : "var(--rose)";
  const gpuHoursBurned = (job.elapsed_s / 3600) * jobGpus.length;
  const wastedHours = gpuHoursBurned * (1 - efficiency / 100);

  if (!jobGpus.length) {
    return (
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}
      >
        <JobHeader job={job} jobs={runningJobs} setJobIdVar={setJobIdVar} />
        <div className="panel" style={{ gridColumn: "span 12" }}>
          <div
            className="panel-body"
            style={{ padding: 40, textAlign: "center", color: "var(--fg-2)" }}
          >
            This job doesn't allocate GPUs (partition ={" "}
            <span className="mono">{job.partition}</span>). GPU timelines are empty. Select a GPU job from the picker.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}>
      <JobHeader
        job={job}
        jobs={runningJobs}
        setJobIdVar={setJobIdVar}
        onDrillNode={onDrillNode}
      />

      {/* Efficiency hero */}
      <div className="panel" style={{ gridColumn: "span 4" }}>
        <div className="panel-hd">
          <span className="panel-title">GPU-hour efficiency</span>
          <span className="chip" style={{ fontSize: 9 }}>
            SBU score · last 2m
          </span>
        </div>
        <div
          className="panel-body"
          style={{
            padding: 14,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
          }}
        >
          <GaugeArc
            value={efficiency}
            max={100}
            unit="%"
            label="EFFICIENCY"
            size={170}
            color={effColor}
            thresholdWarn={45}
            thresholdCrit={25}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              width: "100%",
            }}
          >
            <MicroStat
              label="GPU-hours burned"
              value={gpuHoursBurned.toFixed(2)}
              color="var(--cyan)"
            />
            <MicroStat
              label="Wasted hours"
              value={wastedHours.toFixed(2)}
              color={wastedHours > 0.5 ? "var(--amber)" : "var(--fg-0)"}
            />
            <MicroStat
              label="Peak util"
              value={`${peakUtil.toFixed(0)}%`}
              color="var(--fg-0)"
            />
            <MicroStat
              label="Allocated GPUs"
              value={String(jobGpus.length)}
              color="var(--fg-0)"
            />
          </div>
          <div
            style={{
              fontSize: 10,
              color: "var(--fg-2)",
              lineHeight: 1.5,
              background: "var(--bg-3)",
              padding: 8,
              borderRadius: 4,
              width: "100%",
            }}
          >
            <span style={{ color: "var(--amber)" }}>●</span> Efficiency = avg GPU util
            across allocated GPUs. Low values → pipeline stalls, host-bound loops, or I/O
            saturation.
          </div>
        </div>
      </div>

      {/* util timeline */}
      <div className="panel" style={{ gridColumn: "span 8" }}>
        <div className="panel-hd">
          <span className="panel-title">Per-GPU utilization · allocated fleet</span>
          <span className="chip">{jobGpus.length} GPUs</span>
          <div className="panel-meta">sum/avg · last 3 min</div>
        </div>
        <div className="panel-body">
          <MultiLineChart
            series={jobGpus.map((g, i) => ({
              label: g.id
                .replace("hpc-node-", "n")
                .replace("/gpu", "·g"),
              data: g.util,
              color: PALETTE[i % PALETTE.length],
            }))}
            height={180}
            yMax={100}
            unit="%"
          />
        </div>
      </div>

      {/* HBM */}
      <div className="panel" style={{ gridColumn: "span 6" }}>
        <div className="panel-hd">
          <span className="panel-title">HBM usage · per GPU</span>
          <span className="chip">{totalMem.toFixed(0)} GiB aggregate</span>
        </div>
        <div className="panel-body">
          <MultiLineChart
            series={jobGpus.map((g, i) => ({
              label: g.id
                .replace("hpc-node-", "n")
                .replace("/gpu", "·g"),
              data: g.mem_mb.map((v) => v / 1024),
              color: PALETTE[i % PALETTE.length],
            }))}
            height={140}
            yMax={288}
            unit=" GiB"
            showLegend={false}
          />
          <div
            style={{
              fontSize: 10,
              color: "var(--fg-2)",
              fontFamily: "var(--mono)",
              marginTop: 4,
            }}
          >
            HBM4 288 GiB / pkg ·{" "}
            <span style={{ color: "var(--amber)" }}>watch for checkpointing cliffs</span>
          </div>
        </div>
      </div>

      {/* power */}
      <div className="panel" style={{ gridColumn: "span 6" }}>
        <div className="panel-hd">
          <span className="panel-title">Power draw · per GPU</span>
          <span className="chip">{(totalPower / 1000).toFixed(2)} kW aggregate</span>
        </div>
        <div className="panel-body">
          <MultiLineChart
            series={jobGpus.map((g, i) => ({
              label: g.id
                .replace("hpc-node-", "n")
                .replace("/gpu", "·g"),
              data: g.power_w,
              color: PALETTE[i % PALETTE.length],
            }))}
            height={140}
            yMax={2400}
            unit=" W"
            showLegend={false}
          />
          <div
            style={{
              fontSize: 10,
              color: "var(--fg-2)",
              fontFamily: "var(--mono)",
              marginTop: 4,
            }}
          >
            Rubin pkg TDP 2400 W · idle floor ≈ 110 W · thermal cap kicks ≥ 85 °C
          </div>
        </div>
      </div>

      {/* event timeline */}
      <div className="panel" style={{ gridColumn: "span 8" }}>
        <div className="panel-hd">
          <span className="panel-title">Event timeline</span>
          <span className="chip">scrollable · last 3m</span>
        </div>
        <div className="panel-body">
          <EventTimeline snapshot={snapshot} job={job} />
        </div>
      </div>

      {/* scorecard */}
      <div className="panel" style={{ gridColumn: "span 4" }}>
        <div className="panel-hd">
          <span className="panel-title">Scorecard · auto</span>
          <span className="chip">heuristic</span>
        </div>
        <div className="panel-body">
          <Scorecard
            efficiency={efficiency}
            peak={peakUtil}
            gpus={jobGpus}
            wasted={wastedHours}
          />
        </div>
      </div>
    </div>
  );
}

function JobHeader({
  job,
  jobs,
  setJobIdVar,
  onDrillNode,
}: {
  job: Job;
  jobs: Job[];
  setJobIdVar: (id: number) => void;
  onDrillNode?: (n: string) => void;
}) {
  return (
    <div className="panel" style={{ gridColumn: "span 12" }}>
      <div className="panel-body" style={{ padding: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: "var(--fg-3)",
              fontFamily: "var(--mono)",
            }}
          >
            $job_id
          </div>
          <select
            value={job.id}
            onChange={(e) => setJobIdVar(parseInt(e.target.value, 10))}
            style={{
              height: 28,
              padding: "0 10px",
              background: "var(--bg-3)",
              border: "1px solid var(--line-2)",
              borderRadius: 4,
              color: "var(--cyan)",
              fontFamily: "var(--mono)",
              fontSize: 12,
              minWidth: 260,
            }}
          >
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.id} · {j.name} · {j.user}
              </option>
            ))}
          </select>
          <div className="divider" />
          <div style={{ flex: 1, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <MetaBlock label="JOB NAME" value={job.name} big />
            <MetaBlock label="USER" value={job.user} mono />
            <MetaBlock label="PARTITION" value={job.partition} chip />
            <MetaBlock label="QOS" value={job.qos} mono />
            <MetaBlock
              label="STATE"
              value={job.state}
              chipKind={job.state === "RUNNING" ? "ok" : "warn"}
            />
            <MetaBlock
              label="START"
              value={
                job.started_at
                  ? new Date(job.started_at)
                      .toLocaleString("en-GB")
                      .replace(",", "")
                  : "—"
              }
              mono
              small
            />
            <MetaBlock
              label="ELAPSED"
              value={fmtDuration(job.elapsed_s)}
              mono
              highlight
            />
            <MetaBlock label="WALL LIMIT" value={fmtDuration(job.wall_limit_s)} mono />
            <MetaBlock
              label="NODES"
              custom
              value={
                <div>
                  {job.nodes.map((n) => (
                    <button
                      key={n}
                      className="btn xs"
                      style={{ marginRight: 4 }}
                      onClick={() => onDrillNode?.(n)}
                    >
                      {n.replace("hpc-", "")} ↗
                    </button>
                  ))}
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaBlock({
  label,
  value,
  mono,
  big,
  small,
  chip,
  chipKind,
  highlight,
  custom,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  big?: boolean;
  small?: boolean;
  chip?: boolean;
  chipKind?: string;
  highlight?: boolean;
  custom?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 60 }}>
      <span className="kpi-label" style={{ fontSize: 9 }}>
        {label}
      </span>
      {custom ? (
        <div style={{ marginTop: 2 }}>{value}</div>
      ) : chip ? (
        <span className="chip cyan" style={{ alignSelf: "flex-start" }}>
          {value}
        </span>
      ) : chipKind ? (
        <span className={`chip ${chipKind}`} style={{ alignSelf: "flex-start" }}>
          <span className="dot" />
          {value}
        </span>
      ) : (
        <span
          className={mono ? "mono" : ""}
          style={{
            fontSize: big ? 15 : small ? 11 : 13,
            color: highlight ? "var(--cyan)" : "var(--fg-0)",
            fontWeight: big ? 500 : 400,
          }}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function MicroStat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--line)",
        borderRadius: 3,
        padding: "6px 8px",
      }}
    >
      <div className="kpi-label" style={{ fontSize: 8 }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: 13, color, fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}

function EventTimeline({
  snapshot,
  job,
}: {
  snapshot: ClusterSnapshot;
  job: Job;
}) {
  type Event = { t: number; type: string; label: string; color: string };
  const events: Event[] = [];
  if (job.started_at) {
    events.push({
      t: job.started_at,
      type: "job",
      label: `Job started · srun --gres=gpu:2 -N ${job.nodes.length}`,
      color: "var(--cyan)",
    });
  }
  snapshot.alerts.forEach((a) => {
    const touches =
      (a.labels?.node && job.nodes.includes(a.labels.node)) ||
      a.labels?.jobid === String(job.id);
    if (touches) {
      events.push({
        t: a.fired_at,
        type: "alert",
        label: `${a.name} — ${a.summary}`,
        color:
          a.severity === "critical"
            ? "var(--rose)"
            : a.severity === "warning"
            ? "var(--amber)"
            : "var(--violet)",
      });
    }
  });
  if (job.started_at) {
    const ck1 = job.started_at + 30 * 60_000;
    if (ck1 < Date.now())
      events.push({
        t: ck1,
        type: "ckpt",
        label: `Checkpoint written · /scratch/${job.user}/${job.id}/step-2500.ckpt`,
        color: "var(--emerald)",
      });
    const ck2 = job.started_at + 90 * 60_000;
    if (ck2 < Date.now())
      events.push({
        t: ck2,
        type: "ckpt",
        label: "Checkpoint written · step-5000.ckpt · 84 GiB",
        color: "var(--emerald)",
      });
  }
  events.sort((a, b) => a.t - b.t);

  return (
    <div
      style={{
        position: "relative",
        paddingLeft: 130,
        maxHeight: 170,
        overflow: "auto",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 120,
          top: 4,
          bottom: 4,
          width: 1,
          background: "var(--line-2)",
        }}
      />
      {events.map((e, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 8,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: -10,
              top: "50%",
              transform: "translateY(-50%)",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: e.color,
              boxShadow: `0 0 8px ${e.color}`,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: -130,
              width: 115,
              textAlign: "right",
              fontSize: 10,
              color: "var(--fg-3)",
              fontFamily: "var(--mono)",
            }}
          >
            T+{fmtDuration(Math.max(0, (e.t - job.started_at) / 1000))}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--fg-1)",
              paddingLeft: 10,
            }}
          >
            <span
              className="chip"
              style={{ height: 16, fontSize: 9, marginRight: 6 }}
            >
              {e.type}
            </span>
            {e.label}
          </div>
        </div>
      ))}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 4,
          position: "relative",
        }}
      >
        <div
          className="pulse"
          style={{
            position: "absolute",
            left: -10,
            top: "50%",
            transform: "translateY(-50%)",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--cyan)",
            boxShadow: "0 0 10px var(--cyan)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -130,
            width: 115,
            textAlign: "right",
            fontSize: 10,
            color: "var(--cyan)",
            fontFamily: "var(--mono)",
          }}
        >
          T+{fmtDuration(job.elapsed_s)}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--fg-0)",
            paddingLeft: 10,
            fontFamily: "var(--mono)",
          }}
        >
          now · training · ETA {fmtDuration(Math.max(0, job.wall_limit_s - job.elapsed_s))}
        </div>
      </div>
    </div>
  );
}

function Scorecard({
  efficiency,
  peak,
  gpus,
  wasted,
}: {
  efficiency: number;
  peak: number;
  gpus: GpuSeries[];
  wasted: number;
}) {
  const checks = [
    { ok: efficiency > 70, text: `Average GPU util ${efficiency.toFixed(0)}% · target >70%` },
    { ok: peak > 85, text: `Peak util ${peak.toFixed(0)}% · shows fleet can saturate` },
    { ok: gpus.every((g) => last(g.temp_c) < 85), text: `All allocated GPUs below 85°C thermal threshold` },
    { ok: gpus.every((g) => g.ecc_dbe === 0), text: `No double-bit ECC errors (DBE)` },
    { ok: gpus.every((g) => g.status !== "xid-faulted"), text: `No XID fault on allocated GPUs` },
    { ok: wasted < 1.0, text: `Wasted GPU-hours < 1.0 (${wasted.toFixed(2)}h)` },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {checks.map((c, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            padding: "6px 8px",
            background: "var(--bg-3)",
            border: "1px solid var(--line)",
            borderRadius: 3,
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: c.ok ? "rgba(74,226,138,0.15)" : "rgba(255,184,77,0.15)",
              color: c.ok ? "var(--emerald)" : "var(--amber)",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {c.ok ? "✓" : "!"}
          </div>
          <span style={{ color: c.ok ? "var(--fg-1)" : "var(--fg-0)" }}>
            {c.text}
          </span>
        </div>
      ))}
      <div
        style={{
          marginTop: 6,
          padding: 10,
          background: "rgba(78,225,240,0.06)",
          border: "1px solid rgba(78,225,240,0.25)",
          borderRadius: 4,
          fontSize: 11,
          color: "var(--fg-1)",
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            fontSize: 9,
            letterSpacing: 1,
            color: "var(--cyan)",
            marginBottom: 4,
          }}
        >
          SRE SUGGESTION
        </div>
        {efficiency > 70 ? (
          <>
            Healthy run. Consider scaling{" "}
            <span className="mono">--ntasks-per-node</span> up for the next
            job of the same user.
          </>
        ) : (
          <>
            Efficiency under target. Probable causes: data-loader bottleneck,
            gradient sync over IB saturating, or CPU-side preprocessing. Try{" "}
            <span className="mono">DALI</span> /{" "}
            <span className="mono">NCCL_TOPO_FILE</span>.
          </>
        )}
      </div>
    </div>
  );
}
